import { useCallback, useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';

import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { getLandingHashForAnalytics } from '../../deep-link';
import { getAttribution } from '../attribution';
import { useAnalyticsEvents } from '../generated/use-analytics-events';
import { type AuthState, type SectionName } from '../types';

const DEFAULT_DWELL_THRESHOLD_MS = 1500;
const DEFAULT_INTERSECTION_THRESHOLD = 0.5;

export type UseSectionRevealOptions = {
	/**
	 * IntersectionObserver threshold for "revealed". Defaults to `0.5`
	 * to match the legacy TrackSection behaviour. Footer-style sections
	 * that want a "viewed to bottom" semantic should pass `0` so the
	 * event fires the moment any pixel of the section enters the
	 * viewport.
	 */
	threshold?: number;
	/**
	 * Optional side-effect fired in the same tick as the
	 * `ui.section.revealed.twgMicrositeSection` event. Used by the
	 * Footer to also emit `track.home.viewedToBottom.twgMicrositeHome`
	 * without bloating the hook with section-specific knowledge.
	 *
	 * Receives the active `authState` so callers don't have to pull it
	 * from context themselves.
	 */
	onReveal?: (authState: AuthState) => void;
	/**
	 * Continuous-visibility threshold (ms) after which a separate
	 * `ui.section.dwelled.twgMicrositeSection` event also fires.
	 * Defaults to `1500ms` per the analytics plan — the minimum time
	 * we believe distinguishes a "drive-by" reveal from real
	 * engagement. Set to `0` to disable dwell-tracking for this
	 * section (e.g. for the Footer where dwell is meaningless).
	 */
	dwellThresholdMs?: number;
};

/**
 * Per-section "revealed" + "dwelled" tracker. Returns a stable
 * ref-callback that the section component attaches to its own root
 * layout element (`<Box ref={...}>`, `<div ref={...}>`, etc.).
 *
 * Fires two events from a single shared IntersectionObserver:
 *
 *  1. `ui.section.revealed.twgMicrositeSection` — exactly once per
 *     mount, the first time the element crosses the configured
 *     visibility threshold.
 *  2. `ui.section.dwelled.twgMicrositeSection` — exactly once per
 *     mount, the first time the element has been continuously
 *     visible (above the same threshold) for at least
 *     `dwellThresholdMs`. Cancelled if the section drops back below
 *     the threshold or the page is hidden before the timer elapses.
 *
 * Together they answer "which sections did users glance at"
 * (reveal) vs "which sections did users actually engage with"
 * (dwell).
 *
 * The hook reads auth-state from the surrounding `<UserProvider>` via
 * `useAtlassianUserContext().isAuthed`, so call sites don't have to
 * thread `authState` through props. This keeps section components
 * testable in isolation (just wrap them in a `UserProvider` in tests).
 *
 * Replaces the legacy `<TrackSection>` wrapper component, which
 * introduced an extra `display: block; width: 100%` wrapper div and
 * an a11y-lint-suppressed click handler. The hook approach has zero
 * DOM footprint and lets each section pick its own threshold.
 */
export const useSectionReveal = <T extends Element = HTMLDivElement>(
	sectionName: SectionName,
	options: UseSectionRevealOptions = {},
): React.RefCallback<T> => {
	const {
		threshold = DEFAULT_INTERSECTION_THRESHOLD,
		onReveal,
		dwellThresholdMs = DEFAULT_DWELL_THRESHOLD_MS,
	} = options;
	const { isAuthed } = useAtlassianUserContext();
	const authState: AuthState = isAuthed ? 'loggedIn' : 'loggedOut';
	const { fireEvent } = useAnalyticsEvents();

	const revealFiredRef = useRef(false);
	const dwellFiredRef = useRef(false);
	const visibleSinceMsRef = useRef<number | null>(null);
	const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	// Hold the latest `onReveal` in a ref so swapping callbacks across
	// renders doesn't tear down + rebuild the IntersectionObserver.
	const onRevealRef = useRef(onReveal);
	useEffect(() => {
		onRevealRef.current = onReveal;
	}, [onReveal]);

	// Hold the latest `authState` in a ref for the same reason.
	const authStateRef = useRef(authState);
	useEffect(() => {
		authStateRef.current = authState;
	}, [authState]);

	// Cancel any in-flight dwell timer when the page is hidden so a
	// backgrounded tab doesn't accumulate spurious dwell.
	useEffect(() => {
		if (dwellThresholdMs <= 0) {
			return;
		}
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const onVisibilityChange = (): void => {
			if (doc.visibilityState === 'hidden' && dwellTimerRef.current !== null) {
				clearTimeout(dwellTimerRef.current);
				dwellTimerRef.current = null;
				visibleSinceMsRef.current = null;
			}
		};
		const unbind = bind(doc, { type: 'visibilitychange', listener: onVisibilityChange });
		return (): void => {
			unbind();
		};
	}, [dwellThresholdMs]);

	return useCallback(
		(node: T | null): void => {
			// Detach + re-arm the previous observer when the host node
			// changes (or unmounts).
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
			if (dwellTimerRef.current !== null) {
				clearTimeout(dwellTimerRef.current);
				dwellTimerRef.current = null;
			}
			visibleSinceMsRef.current = null;

			// If both events have already fired, no need to re-observe.
			const dwellEnabled = dwellThresholdMs > 0;
			const allDone = revealFiredRef.current && (!dwellEnabled || dwellFiredRef.current);
			if (node === null || allDone) {
				return;
			}
			// IntersectionObserver may be missing in older test envs; bail out
			// silently rather than crashing the component.
			if (typeof IntersectionObserver === 'undefined') {
				return;
			}

			const fireDwell = (): void => {
				if (dwellFiredRef.current || visibleSinceMsRef.current === null) {
					return;
				}
				const dwellMs = Math.max(0, Date.now() - visibleSinceMsRef.current);
				dwellFiredRef.current = true;
				fireEvent('ui.section.dwelled.twgMicrositeSection', {
					sectionName,
					dwellMs,
					authState: authStateRef.current,
					landingHash: getLandingHashForAnalytics(),
					...getAttribution(),
				});
				// If reveal also already fired, we're done — disconnect.
				if (revealFiredRef.current && observerRef.current) {
					observerRef.current.disconnect();
					observerRef.current = null;
				}
			};

			const io = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.isIntersecting) {
							// Reveal — fires on first intersection.
							if (!revealFiredRef.current) {
								revealFiredRef.current = true;
								const currentAuthState = authStateRef.current;
								fireEvent('ui.section.revealed.twgMicrositeSection', {
									sectionName,
									authState: currentAuthState,
									landingHash: getLandingHashForAnalytics(),
								});
								onRevealRef.current?.(currentAuthState);
								// If dwell is disabled and reveal just fired,
								// we can disconnect immediately.
								if (!dwellEnabled) {
									io.disconnect();
									observerRef.current = null;
									return;
								}
							}
							// Dwell — start the visibility timer.
							if (dwellEnabled && !dwellFiredRef.current && visibleSinceMsRef.current === null) {
								visibleSinceMsRef.current = Date.now();
								dwellTimerRef.current = setTimeout(fireDwell, dwellThresholdMs);
							}
						} else {
							// Cancel an in-flight dwell timer if the
							// section drops back below threshold before
							// the dwell event fires.
							if (dwellTimerRef.current !== null) {
								clearTimeout(dwellTimerRef.current);
								dwellTimerRef.current = null;
							}
							visibleSinceMsRef.current = null;
						}
					}
				},
				{ threshold },
			);
			io.observe(node);
			observerRef.current = io;
		},
		[fireEvent, sectionName, threshold, dwellThresholdMs],
	);
};
